import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Undo2 } from "lucide-react";
import DateInput from "@/components/DateInput";
import { fmtDate, todayISO, cn } from "@/lib/utils";
import { STEPS } from "@/lib/contracts";
import { useApprovers } from "@/components/contracts/Approvers";

export function ApprovalFlow({ payment, onChanged }) {
  const { list } = useApprovers();
  const idx = Math.max(STEPS.findIndex(s => s[0] === (payment.status || "hazirlandi")), 0);
  const next = STEPS[idx + 1];
  const done = Object.fromEntries((payment.approvals || []).map(a => [a.step, a]));
  const [f, setF] = useState({ person_id: "", date: todayISO(), note: "" });
  const people = next ? list.filter(a => a.roles.includes(next[0])) : [];

  const advance = async () => {
    if (!f.person_id) { toast.error("Kişi seçin"); return; }
    try { await api.post(`/contract-payments/${payment.id}/step`, { step: next[0], ...f }); toast.success(`${next[1]} olarak işaretlendi`); setF({ ...f, person_id: "", note: "" }); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };
  const revert = async () => {
    const note = window.prompt("Geri alma nedeni (isteğe bağlı):", "");
    if (note === null) return;
    try { await api.post(`/contract-payments/${payment.id}/revert`, { note }); toast.success("Adım geri alındı"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Geri alınamadı"); }
  };

  return (
    <div className="rounded-lg border border-border p-3" data-testid="approval-flow">
      <div className="grid grid-cols-4 gap-2">
        {STEPS.map(([k, l], i) => (
          <div key={k} className={cn("rounded-md p-2 text-xs border", i <= idx ? "border-emerald-500/50 bg-emerald-500/10" : "border-border")} data-testid={`step-${k}`}>
            <div className="flex items-center gap-1 font-semibold">{i <= idx && <Check className="w-3.5 h-3.5 text-emerald-600" />}{l}</div>
            {done[k] ? <div className="mt-1">{done[k].person_name}<div className="text-muted-foreground">{fmtDate(done[k].date)}</div>{done[k].note && <div className="italic text-muted-foreground">{done[k].note}</div>}</div>
              : i <= idx && <div className="mt-1 text-muted-foreground">kayıt yok</div>}
          </div>
        ))}
      </div>
      {next && (
        <div className="flex flex-wrap items-end gap-2 mt-3">
          <div className="text-xs font-semibold w-full">Sonraki adım: {next[1]}</div>
          <Select value={f.person_id} onValueChange={v => setF({ ...f, person_id: v })}>
            <SelectTrigger className="w-48 h-9" data-testid="step-person"><SelectValue placeholder={people.length ? "Kişi seçin" : "Yetkili kişi yok"} /></SelectTrigger>
            <SelectContent>{people.map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.title && ` · ${a.title}`}</SelectItem>)}</SelectContent>
          </Select>
          <div className="w-36"><DateInput value={f.date} onChange={e => setF({ ...f, date: e.target.value })} data-testid="step-date" /></div>
          <Input className="flex-1 min-w-[140px] h-9" placeholder="Not" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} data-testid="step-note" />
          <Button size="sm" onClick={advance} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="step-advance">{next[1]}</Button>
        </div>
      )}
      {idx > 0 && <Button size="sm" variant="ghost" onClick={revert} className="mt-2 text-rose-600" data-testid="step-revert"><Undo2 className="w-4 h-4 mr-1" /> Son adımı geri al</Button>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const i = Math.max(STEPS.findIndex(s => s[0] === (status || "hazirlandi")), 0);
  const tone = ["bg-slate-500/15 text-slate-700 dark:text-slate-300", "bg-sky-500/15 text-sky-700 dark:text-sky-300", "bg-amber-500/15 text-amber-700 dark:text-amber-300", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"][i];
  return <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap", tone)} data-testid="status-badge">{STEPS[i][1]}</span>;
}
